import React from "react";
import Editor from "@monaco-editor/react";
import { Play, Loader2, AlertTriangle } from "lucide-react";

const ACCENT_BTN = { indigo: "bg-primary hover:bg-primary/90", teal: "bg-teal-500 hover:bg-teal-400", fuchsia: "bg-fuchsia-500 hover:bg-fuchsia-400" };

export default function SqlSandboxPanel({ value, onChange, onRun, running, result, accent = "indigo" }) {
  const btnClass = ACCENT_BTN[accent] || ACCENT_BTN.indigo;

  return (
    <div className="glass-panel rounded-2xl overflow-hidden" data-testid="sql-sandbox-panel">
      <div className="border-b border-white/10">
        <Editor
          height="180px"
          defaultLanguage="sql"
          theme="vs-dark"
          value={value}
          onChange={(v) => onChange(v || "")}
          options={{ minimap: { enabled: false }, fontSize: 13, lineNumbers: "on", scrollBeyondLastLine: false }}
          data-testid="sql-monaco-editor"
        />
      </div>
      <div className="p-3 flex items-center justify-between">
        <button
          onClick={onRun}
          disabled={running || !value.trim()}
          data-testid="sql-run-btn"
          className={`flex items-center gap-2 ${btnClass} disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors`}
        >
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Run Query
        </button>
        {result && result.success && <span className="text-xs text-slate-400">{result.row_count} rows · {result.execution_time_ms}ms</span>}
      </div>

      {result && (
        <div className="border-t border-white/10 p-3 max-h-56 overflow-auto" data-testid="sql-result-panel">
          {result.success ? (
            <table className="w-full text-xs text-left">
              <thead>
                <tr className="text-slate-500">
                  {result.columns.map((c) => <th key={c} className="pb-2 pr-4 font-semibold">{c}</th>)}
                </tr>
              </thead>
              <tbody>
                {result.rows.map((row, i) => (
                  <tr key={i} className="border-t border-white/5 text-slate-300">
                    {row.map((cell, j) => <td key={j} className="py-1.5 pr-4">{String(cell)}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="flex items-start gap-2 text-sm text-rose-300" data-testid="sql-error-message">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <div>
                <p>{result.error.message}</p>
                {result.error.ai_explanation && <p className="text-xs text-slate-400 mt-1">{result.error.ai_explanation}</p>}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
